/**
 * Die Gliederung des Gewerks Außenanlagen — an EINER Stelle.
 * Muster N1 (Kopfkommentar `seiten/heizlastkuehllast/abschnitte.ts`),
 * umgesetzt in Paket N2.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ACHT REITER, NICHT DIE 18 AUS DER TABELLENZAHL DES PLANS
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Plan Abschnitt 3.4 nennt „18 Reiter-Spuren" — das ist ein Grep-Treffer
 * über verschachtelte Komponenten, nicht die Zahl der ECHTEN Reiter.
 * Gemessen an `Aussenanlagen.tsx` (`Abschnitte`, jetzt aufgelöst) gibt es
 * genau ACHT: Bestand, Sanitär, Elektro, Hydranten, Geräte, Versickerung,
 * Berechnungen, Nachweis. Die vom Plan vorgeschlagenen Unterpunkte
 * „Löschwasser", „Teilstrecken" und „Fernwärme" sind KEINE eigenen Seiten,
 * sondern Bereiche INNERHALB der einen Berechnungsseite (`AussenBerechnung`,
 * Auswahl `freispiegel | loeschwasser | fernwaerme | versickerung |
 * trinkwasser | leerrohr`) — Navigation verweist nur auf real Vorhandenes.
 *
 * `AussenBerechnung.tsx` und `AussenNachweis.tsx` hatten bis Paket N2 KEINE
 * eigene Route — sie waren Tab-Zustand (`abschnitt === 'berechnung'` bzw.
 * `'nachweis'`) auf der einen Seite `aussenanlagen`. Beide sind
 * self-fetching (nur `api`+`projektId` als Props, wie schon vor N2) und
 * tragen jetzt beide eine eigene Route, ohne Umbau ihres Inhalts.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * DIE VIER STANDARD-ZWISCHENZEILEN, GEMESSEN VERTEILT
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Planung: Sanitär (Leitungen + Schächte) — die Erschließungsleitungen sind
 * das Hauptnetz, aus dem die Berechnung folgt (Plan: „planung: Leitungen,
 * Schächte").
 *
 * Berechnungen: die eine Berechnungsseite, die Versickerung, Löschwasser,
 * Freispiegelentwässerung, Fernwärme, Trinkwasser und Leerrohrnetz als
 * Bereiche trägt (s. o.) — das macht „Versickerung und Löschwasser bei den
 * Außenanlagen" sichtbar (Auftrag N2), ohne sie zu erfinden.
 *
 * Daten: Bestand (der Index — fällt in `alleEintraege` automatisch heraus),
 * Elektro (Leerrohre + Schächte, reine Bestandslisten, Plan nennt
 * „Leerrohre" explizit unter daten), Hydranten (die physischen
 * Überflurhydranten — die BERECHNETE Löschwasseranforderung liegt in der
 * Berechnungsseite, der Nachweis in Nachweis), Geräte (Produktdaten),
 * Versickerung (die Bauwerksliste mit Volumen/kf-Wert je Bauwerk — Bestand
 * mit einem Ist/Soll-Vergleich je Zeile, keine eigene Bemessung; die
 * BEMESSUNG selbst läuft über die Berechnungsseite).
 *
 * Prüfung: Nachweis (`AussenNachweis.tsx`) — genau der Plan-Vorschlag.
 */

export interface AussenanlagenEintrag {
  /** Der Pfad UNTER /projekte/:id/aussenanlagen/ (bzw. 'aussenanlagen' selbst fuer den Index). */
  pfad: string;
  name: string;
  /** Wozu die Seite da ist. Erscheint als Titel am Baumeintrag. */
  hinweis: string;
}

export interface AussenanlagenAbschnitt {
  name: string;
  eintraege: AussenanlagenEintrag[];
}

export const AUSSENANLAGEN_ABSCHNITTE: AussenanlagenAbschnitt[] = [
  {
    name: 'Planung',
    eintraege: [
      {
        pfad: 'aussenanlagen/sanitaer',
        name: 'Sanitär',
        hinweis: 'Leitungen (nach Kreis filterbar) und Schächte der '
          + 'sanitären Erschließung',
      },
    ],
  },
  {
    name: 'Berechnungen',
    eintraege: [
      {
        pfad: 'aussenanlagen/berechnung',
        name: 'Berechnungen',
        hinweis: 'Freispiegelentwässerung, Löschwasser, Fernwärme, '
          + 'Versickerung, Trinkwasser und Leerrohrnetz — je Bereich mit '
          + 'Annahmen, Strängen und Teilstrecken',
      },
      {
        // TS9b2: die Rückhaltung je Einleitstelle (DWA-A 117) steht im
        // Versickerungslauf, hat aber eine eigene Route auf dem Server -
        // ihr Abfluss wird genehmigt, nicht gemessen (AUA-01/02).
        pfad: 'aussenanlagen/rueckhaltung',
        name: 'Rückhaltung',
        hinweis: 'Die Rückhaltung je Einleitstelle nach DWA-A 117 — '
          + 'Volumen, Drosselabfluss und Deckung',
      },
    ],
  },
  {
    name: 'Daten',
    eintraege: [
      {
        // Index-Route — pfad ist absichtlich der Fachbereichspfad selbst,
        // siehe Kopfkommentar Punkt 3 in heizlastkuehllast/abschnitte.ts.
        pfad: 'aussenanlagen',
        name: 'Bestand',
        hinweis: 'Der Bestand in Kennzahlen: Rohre, Schächte, Netze — '
          + 'zwei getrennte Netze aus demselben Modell',
      },
      {
        pfad: 'aussenanlagen/elektro',
        name: 'Elektro',
        hinweis: 'Leerrohre und Elektroschächte',
      },
      {
        pfad: 'aussenanlagen/hydranten',
        name: 'Hydranten',
        hinweis: 'Die Überflurhydranten im Bestand',
      },
      {
        pfad: 'aussenanlagen/geraete',
        name: 'Geräte',
        hinweis: 'Fabrikate und Kennwerte — dieselbe Detailseite wie bei '
          + 'Heizung, Lüftung und Sanitär',
      },
      {
        pfad: 'aussenanlagen/versickerung',
        name: 'Versickerung',
        hinweis: 'Die Versickerungs- und Rückhaltebauwerke: Volumen im '
          + 'Modell gegen erforderliches Volumen, kf-Wert, Einzugsflächen',
      },
    ],
  },
  {
    name: 'Prüfung',
    eintraege: [
      {
        pfad: 'aussenanlagen/nachweis',
        name: 'Nachweis',
        hinweis: 'Der abgelegte Nachweis je Strang — leer ohne '
          + 'vorherigen Berechnungslauf',
      },
    ],
  },
];
